import type { BannerType } from '@sunwebgroup/ui-components';
import {type CheckoutServiceOption, ServiceType} from '../../domain/types';
import * as React from 'react';
import { styled } from 'styled-components';
import { useSessionStorage } from 'react-use';
import {isEmpty} from "ramda";
import {
  ListItem,
  RadioGroup,
  SpaceBetween,
  tk,
  Typography,
  Banner,
  ChoiceItem,
  Switch,
} from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import {useCheckoutSid, useService} from '@/scopes/checkout/domain/hooks';

import ServiceBox from './ServiceBox';
import {updatePerParticipantService, updatePerBookingService} from "../../domain/actions";
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable';
import { createAncillariesClickedEvent, createGenericServiceEvent } from '../../domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import {
  ParticipantName,
  Price,
  RoomName,
  SwitchLabel,
  Texts,
} from '@/scopes/checkout/components/services/ServiceComponents';
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon';
import { Icon } from '@sunwebgroup/ui-components/uds';

const StyledTableContainer = styled.div`
  margin-top: 24px;
  font-size: 11px;
  letter-spacing: .33px;
  line-height: 1.2;
  font-style: normal;
  font-weight: 500;
  margin-bottom: 0;
`;
const StyledTable = styled.div`
  display: grid;
  grid-template-columns: 2fr repeat(3, 1fr);
  font-size: 11px;
  letter-spacing: .33px;
  line-height: 1.2;
  font-style: normal;
  font-weight: 500;
`;
const StyledTableColumn = styled.div`
  margin-bottom: 24px;
  display: grid;
  grid-auto-rows: 1fr;
  grid-template-columns: 1fr;
`;
const StyledTableItemFirst = styled.div`
  border-bottom: 1px solid #fbf9f6;
  align-items: center;
  border-right: none;
  border-top: none;
  display: flex;
  font-weight: 700;
  justify-content: center;
  font-size: 18px;
  letter-spacing: 0;
`;
const StyledTableItem = styled.div`
  grid-gap: 8px;
  align-items: center;
  border-bottom: 1px solid #fbf9f6;
  border-right: 1px solid #fbf9f6;
  display: flex;
  padding: 8px;
`;

  export function TravelInsuranceService() {
  const intl = useIntl();
  const sid = useCheckoutSid();
  const { service } = useService(ServiceType.TravelInsurance);
  const { sendEvent } = useAnalytics();

  const [selectPerParticipantChecked, setSelectPerParticipantChecked] = useSessionStorage(
    `${sid}-travel-insurance-is-per-participant`,
    true
  );

  const handleSwitchPerParticipantChange = React.useCallback(
    () => {
      setSelectPerParticipantChecked(!selectPerParticipantChecked);
      sendEvent(
        createGenericServiceEvent({
          description: 'Choose per participant',
          element: 'toggle',
          type: 'selectTravelInsurance',
          result: !selectPerParticipantChecked ? 'element selected' : 'element unselected',
        })
      );
    },
    [setSelectPerParticipantChecked, selectPerParticipantChecked, sendEvent]
  );

  React.useEffect(() => {
    if (!service) return;
    if (service.participants && Object.keys(service.participants).length === 1) {
      setSelectPerParticipantChecked(false);
    }
  })
  useSendAncillaryAvailable(service, 'selectTravelInsurance');

  if (!service) return null;

  const ModalDescription = (
  <>
    <Typography $variant="body-md">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_header_text' })}</Typography>
    <StyledTableContainer>
      <StyledTable>
        <StyledTableColumn>
          <StyledTableItem>&nbsp;</StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_1' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_2' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_3' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_4' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_5' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_6' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_7' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_8' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_9' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_10' })}</Typography></StyledTableItem>
          <StyledTableItem><Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_feature_11' })}</Typography></StyledTableItem>
        </StyledTableColumn>
        <StyledTableColumn>
          <StyledTableItemFirst><Typography $variant="body-md">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_base_header' })}</Typography></StyledTableItemFirst>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 1.000</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 100</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 500</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="Dismiss" color="#ed2924"/><Typography $variant="body-sm"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_no_compensation_label' })}</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="Dismiss" color="#ed2924"/><Typography $variant="body-sm"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_no_compensation_label' })}</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="Dismiss" color="#ed2924"/><Typography $variant="body-sm"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_no_compensation_label' })}</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="Dismiss" color="#ed2924"/><Typography $variant="body-sm"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_no_label' })}</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 100</Typography></StyledTableItem>
        </StyledTableColumn>
        <StyledTableColumn>
          <StyledTableItemFirst><Typography $variant="body-md">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_comfort_header' })}</Typography></StyledTableItemFirst>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 3.000</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 300</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 1.500</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_one_time_fee_label' })}</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 25.000</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 75.000</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="Dismiss" color="#ed2924"/><Typography $variant="body-sm"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_no_label' })}</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 50</Typography></StyledTableItem>
        </StyledTableColumn>
        <StyledTableColumn>
          <StyledTableItemFirst><Typography $variant="body-md">{intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_optimal_header' })}</Typography></StyledTableItemFirst>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 5.500</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 500</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 2.500</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_one_time_fee_label' })}</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 25.000</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 75.000</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="CheckMark" color="#21b788"/><Typography $variant="body-sm"> € 500</Typography></StyledTableItem>
          <StyledTableItem><Icon glyph="Dismiss" color="#ed2924"/><Typography $variant="body-sm"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_non_label' })}</Typography></StyledTableItem>
        </StyledTableColumn>
      </StyledTable>
      <Typography $variant="body-md"
        dangerouslySetInnerHTML={{
        __html: intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_footer_text' }),
        }} />
      <br/>
      <br/>
      <Typography $variant="body-md"
        dangerouslySetInnerHTML={{
          __html: intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_footer_text_2' }),
        }} />
    </StyledTableContainer>
  </>
  );

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_description' })}
      glyph={getIconForService(ServiceType.TravelInsurance)}
      modalTitle={intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_modal_title' })}
      modalDescriptionElement={ModalDescription}
    >
      <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
        { service.participants && Object.keys(service.participants).length > 1 && (
          <ChoiceItem.Root>
            <ChoiceItem.Control>
              <Switch checked={selectPerParticipantChecked} onCheckedChange={handleSwitchPerParticipantChange} />
            </ChoiceItem.Control>
            <SwitchLabel>
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_applies_to_all_participants' })}
            </SwitchLabel>
          </ChoiceItem.Root>
        )}
        {selectPerParticipantChecked ? <TravelInsuranceServicePerParticipant /> : <TravelInsuranceServicePerBooking />}
      </SpaceBetween>
    </ServiceBox>
  );
}

function TravelInsuranceServicePerParticipant() {
  const { service, setServiceValue } = useService(ServiceType.TravelInsurance);
  const { sendEvent } = useAnalytics();

  const handleValueChange = React.useCallback(
    (participantId: string, value: string) => {
      if (!service) return;
      setServiceValue(updatePerParticipantService({ participantId, value })(service));
      const optionClicked = service.options[value]
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label ? `${optionClicked?.label} for participant ${participantId}` : '',
          description: 'selectTravelInsurance',
          element: 'radiobutton',
        })
      );
    },
    [sendEvent, setServiceValue, service]
  );

  if (!service) return null;

  const { options, rooms, participants, banners } = service

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      {rooms.map(({ id: roomId, name: roomName, participantIds }) => (
        <SpaceBetween key={roomId} direction="y" spacing={tk('spacing_md')}>
          {roomName ? <RoomName as="p" $variant="body-sm-bold">
            {roomName}
          </RoomName> : null}
          {participantIds.map((participantId) => {
            const participant = participants?.[participantId];
            if (!participant || participant.availableOptions.length === 0) {
              return null;
            }

            return (
              <ParticipantRow
                {...participant}
                key={participantId}
                options={options}
                onValueChange={handleValueChange}
              />
            );
          })}
        </SpaceBetween>
      ))}
      {isEmpty(rooms) &&
        Object.values(participants ?? {}).map((participant) => (
          <ParticipantRow {...participant} key={participant.id} options={options} onValueChange={handleValueChange} />
        ))}
      {banners.map(({ title, text, type = 'info' }, index) => (
        <Banner key={`banner-${index}`} $type={type as BannerType}>
          <Texts direction="y">
            {title && <Typography $variant="label">{title}</Typography>}
            {text && <Typography dangerouslySetInnerHTML={{ __html: text }} />}
          </Texts>
        </Banner>
      ))}
    </SpaceBetween>
  );
}

function ParticipantRow({
  id,
  options,
  availableOptions,
  selectedValue,
  name,
  onValueChange,
}: {
  id: string;
  options: Record<string, CheckoutServiceOption>;
  availableOptions: string[];
  name: string;
  selectedValue: string | null;
  onValueChange: (participantId: string, value: string) => void;
}) {
  const intl = useIntl();

  const handleValueChange = React.useCallback(
    (value: string) => {
      onValueChange(id, value);
    },
    [id, onValueChange]
  );

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
      <ParticipantName as="span" $variant="body-xs">
        {name}
      </ParticipantName>
      <RadioGroup.Root value={selectedValue ?? null} onValueChange={handleValueChange}>
        <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
          {availableOptions.map((optionId) => {
            const { value, label, badge, price } = options[optionId];

            return (
              <ListItem.Root key={optionId}>
                <ListItem.Control>
                  <RadioGroup.Item value={value} />
                </ListItem.Control>
                <ListItem.Content>
                  {badge && <ListItem.Badge $state="success">{badge}</ListItem.Badge>}
                  <ListItem.Label>{label}</ListItem.Label>
                </ListItem.Content>
                <ListItem.Trailing>
                  <Price as="span" $variant="label">
                    {intl.formatCurrency(price)}
                  </Price>
                </ListItem.Trailing>
              </ListItem.Root>
            );
          })}
        </SpaceBetween>
      </RadioGroup.Root>
    </SpaceBetween>
  );
}

function TravelInsuranceServicePerBooking() {
  const intl = useIntl();
  const { service, setServiceValue } = useService(ServiceType.TravelInsurancePerBooking);
  const { sendEvent } = useAnalytics();

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service) return;
      setServiceValue(updatePerBookingService({ value })(service));
      const optionClicked = service.options.find((option) => option.value === value);
      sendEvent(
        createAncillariesClickedEvent({
          result: (Object.keys(service.participants).length) * (optionClicked?.price || 0),
          type: optionClicked?.label || '',
          description: 'selectTravelInsurance',
          element: 'radiobutton',
        })
      );
    },
    [sendEvent, setServiceValue, service]
  );

  if (!service) return null;

  const { options, selectedValue, banners } = service

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      <RadioGroup.Root value={selectedValue ?? null} onValueChange={handleValueChange}>
        <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
          {options.map(({ label, badge, price, value, priceBreakdown }) => (
            <ListItem.Root key={value}>
              <ListItem.Control>
                <RadioGroup.Item value={value} />
              </ListItem.Control>
              <ListItem.Content>
                {badge && <ListItem.Badge $state="success">{badge}</ListItem.Badge>}
                <ListItem.Label>{label}</ListItem.Label>
              </ListItem.Content>
              <ListItem.Trailing>
                {priceBreakdown && priceBreakdown.length > 0 ? (
                  priceBreakdown?.map(({ ageCategory, price: pricePerAgeCategory }) => (
                    <Price key={ageCategory} as="p" $variant="label">
                      {intl.formatMessage({
                        id: `checkout_steps_ancillaries_travel_insurance_age_category_${ageCategory}`,
                      })}{' '}
                      {intl.formatMessage({ id: 'checkout_steps_price_per_person'}, { price: intl.formatCurrency(pricePerAgeCategory) })}
                    </Price>
                  ))
                ) : (
                  <Price as="span" $variant="label">
                    {intl.formatMessage({ id: 'checkout_steps_price_per_person'}, { price: intl.formatCurrency(price) })}
                  </Price>
                )}
              </ListItem.Trailing>
            </ListItem.Root>
          ))}
        </SpaceBetween>
      </RadioGroup.Root>
      {banners.map(({ title, text, type = 'info' }) => (
        <Banner key={title} $type={type as BannerType}>
          <Texts direction="y">
            {title && <Typography $variant="label">{title}</Typography>}
            {text && <Typography dangerouslySetInnerHTML={{ __html: text }} />}
          </Texts>
        </Banner>
      ))}
    </SpaceBetween>
  );
}
